import type { AttackProfile, Item, Skill } from '@dcc/rules/content/types.ts';
import { CUSTOM_SKILL_LIBRARY_SCHEMA_VERSION, validateCustomSkillLibrary } from '@dcc/rules/domain/customSkill.ts';
import type { Character } from '@dcc/rules/domain/character.ts';
import { CUSTOM_ITEM_LIBRARY_SCHEMA_VERSION, validateCustomItemLibrary } from '@dcc/rules/domain/customItem.ts';
import { hotlistAllocation } from '@dcc/rules/rules/hotlist.ts';
import { unallocatedCopies, type InventoryEntry } from '@dcc/rules/rules/inventory.ts';

/** IDs the sheet server gives Items that belong to the room (server/sheets/inventory.js). */
export const ROOM_ITEM_PREFIX = 'custom:room-';

export function isRoomItemId(itemId: string): boolean {
  return itemId.startsWith(ROOM_ITEM_PREFIX);
}

/**
 * The room Item definitions this sheet can safely compose into its catalogue.
 *
 * The server validates every definition before storing it, with the engine's
 * own validator. This checks again, one Item at a time, because a definition
 * that a NEWER server accepted and this build cannot read must cost the player
 * that one Item - not their whole catalogue.
 */
export function usableRoomItems(raw: readonly unknown[]): { items: Item[]; problems: string[] } {
  const items: Item[] = [];
  const problems: string[] = [];
  const seen = new Set<string>();
  for (const entry of raw) {
    const candidate = entry && typeof entry === 'object' && !Array.isArray(entry) ? entry as Record<string, unknown> : null;
    const id = candidate && typeof candidate['id'] === 'string' ? candidate['id'] : null;
    const name = candidate && typeof candidate['name'] === 'string' ? candidate['name'] : 'An Item';
    if (id === null || !isRoomItemId(id) || seen.has(id)) {
      problems.push(`${name} from the room could not be loaded: it has no usable room ID.`);
      continue;
    }
    const report = validateCustomItemLibrary({
      kind: 'custom-item-library', schemaVersion: CUSTOM_ITEM_LIBRARY_SCHEMA_VERSION, items: [candidate], createdAt: 'x', updatedAt: 'x',
    });
    if (!report.valid) {
      problems.push(`${name} from the room could not be loaded by this version of the sheet.`);
      continue;
    }
    seen.add(id);
    items.push(candidate as unknown as Item);
  }
  return { items, problems };
}

/**
 * Copies of an Item a crawler could hand to the party right now.
 *
 * The same arithmetic the server applies (RULE DCC-INV-019: every copy is in
 * exactly one place): owned, minus worn, minus held, minus prepared on the
 * Hotlist, minus assigned to a companion. Shown so the button is honest; the
 * server decides.
 */
export function freeCopies(character: Pick<Character, 'hotlist' | 'companions'>, entry: InventoryEntry): number {
  const withCompanions = (character.companions ?? []).filter((pet) => pet.gearItemIds.includes(entry.itemId)).length;
  return Math.max(0, unallocatedCopies(entry) - hotlistAllocation(character.hotlist, entry.itemId) - withCompanions);
}

/** A whole number from 1 to `max`, however the quantity box was typed in. */
export function clampQuantity(value: string, max: number): number {
  const parsed = Math.floor(Number(value));
  if (!Number.isFinite(parsed) || parsed < 1) return 1;
  return Math.min(parsed, Math.max(1, max));
}

/**
 * The Skill and Attack halves of GM-made weapons, checked with the engine's own
 * custom Skill validator - the bar a player's own custom weapon has to clear.
 * A pair that fails is left out together: a Skill with a broken Attack would
 * be a weapon that silently cannot be swung.
 */
export function usableRoomWeapons(
  rawSkills: readonly unknown[],
  rawAttacks: readonly unknown[],
): { skills: Skill[]; attacks: AttackProfile[]; problems: string[] } {
  const skills: Skill[] = [];
  const attacks: AttackProfile[] = [];
  const problems: string[] = [];
  const idOf = (entry: unknown): string | null => {
    const candidate = entry && typeof entry === 'object' && !Array.isArray(entry) ? entry as Record<string, unknown> : null;
    return candidate && typeof candidate['id'] === 'string' ? candidate['id'] : null;
  };
  const seen = new Set<string>();
  for (const skill of rawSkills) {
    const id = idOf(skill);
    const name = id !== null && typeof (skill as Record<string, unknown>)['name'] === 'string' ? String((skill as Record<string, unknown>)['name']) : 'A weapon';
    const attack = rawAttacks.find((entry) => idOf(entry) === id);
    if (id === null || !isRoomItemId(id) || seen.has(id) || attack === undefined) {
      problems.push(`${name} from the room could not be loaded as a weapon.`);
      continue;
    }
    const report = validateCustomSkillLibrary({
      kind: 'custom-skill-library', schemaVersion: CUSTOM_SKILL_LIBRARY_SCHEMA_VERSION, skills: [skill], attacks: [attack], createdAt: 'x', updatedAt: 'x',
    });
    if (!report.valid) {
      problems.push(`${name} from the room could not be loaded as a weapon by this version of the sheet.`);
      continue;
    }
    seen.add(id);
    skills.push(skill as Skill);
    attacks.push(attack as AttackProfile);
  }
  return { skills, attacks, problems };
}
